import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { FolderKanban, Clock, CheckCircle2, Edit2, Save, X, Sparkles, UserCheck } from 'lucide-react';
import { PUBLISHING_STAGES_CONFIG, ProjectStatus, MilestoneStatus } from '@ftt/shared';

export const AdminProjectsPage: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [projectStatus, setProjectStatus] = useState<string>('');
  const [targetPubDate, setTargetPubDate] = useState<string>('');
  const [assignedStaffId, setAssignedStaffId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  const queryClient = useQueryClient();

  const { data: projectsRes, isLoading } = useQuery({
    queryKey: ['admin-projects'],
    queryFn: () => api.get('/admin/projects').then((res) => res.data.data),
  });

  const { data: staffRes } = useQuery({
    queryKey: ['admin-staff'],
    queryFn: () => api.get('/admin/staff').then((res) => res.data.data),
  });

  const staffList = staffRes || [];
  const projects = projectsRes || [];

  const updateProjectMutation = useMutation({
    mutationFn: async () => {
      return api.put(`/admin/projects/${selectedProject.id}`, {
        status: projectStatus,
        targetPubDate: targetPubDate || null,
        assignedStaffId: assignedStaffId || null,
        notes,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-projects'] });
      setSelectedProject(null);
    },
  });

  const updateMilestoneMutation = useMutation({
    mutationFn: async ({ milestoneId, status }: { milestoneId: string; status: MilestoneStatus }) => {
      return api.put(`/admin/milestones/${milestoneId}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-projects'] });
    },
  });

  const openProjectEditor = (p: any) => {
    setSelectedProject(p);
    setProjectStatus(p.status);
    setTargetPubDate(p.targetPubDate ? p.targetPubDate.substring(0, 10) : '');
    setAssignedStaffId(p.assignedStaffId || '');
    setNotes(p.notes || '');
  };

  return (
    <div className="space-y-8">
      
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white">
          Publishing Project Management
        </h1>
        <p className="text-xs sm:text-sm text-slate-400">
          Supervise project lifecycles, advance 12-stage milestones, and allocate senior editors.
        </p>
      </div>

      {/* Projects Grid */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-400">Loading projects...</div>
      ) : projects.length === 0 ? (
        <div className="p-12 rounded-2xl bg-slate-950 border border-slate-800 text-center space-y-3">
          <FolderKanban className="w-12 h-12 text-slate-600 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-white">No Projects In Pipeline</h3>
        </div>
      ) : (
        <div className="space-y-6">
          {projects.map((proj: any) => {
            const completedCount = proj.milestones?.filter((m: any) => m.status === 'COMPLETED').length || 0;
            const totalCount = proj.milestones?.length || 12;
            const pct = Math.round((completedCount / totalCount) * 100);

            return (
              <div
                key={proj.id}
                className="p-6 sm:p-8 rounded-2xl bg-slate-950 border border-slate-800 space-y-6 shadow-xl"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono px-2.5 py-0.5 rounded bg-slate-900 border border-slate-700 text-brand-400 font-bold">
                        {proj.projectCode}
                      </span>
                      <span className="text-xs text-slate-400 font-mono">
                        {proj.category}
                      </span>
                    </div>
                    <h2 className="font-serif text-2xl font-bold text-white">
                      {proj.title}
                    </h2>
                    <p className="text-xs text-slate-400">
                      Author: <strong className="text-slate-200">{proj.author}</strong> &bull; Client: {proj.client?.user?.firstName} {proj.client?.user?.lastName} ({proj.client?.user?.email})
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => openProjectEditor(proj)}
                      className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs flex items-center gap-1.5 transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      <span>Edit Status &amp; Lead</span>
                    </button>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-slate-400">
                      12-Stage Milestones ({completedCount} of {totalCount} Completed)
                    </span>
                    <span className="font-bold text-brand-400">{pct}%</span>
                  </div>
                  <div className="w-full bg-slate-900 rounded-full h-2 overflow-hidden border border-slate-800">
                    <div className="bg-brand-500 h-2 rounded-full" style={{ width: `${pct}%` }}></div>
                  </div>
                </div>

                {/* Quick Milestones Checklist */}
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {proj.milestones?.map((m: any) => {
                    const isDone = m.status === 'COMPLETED';
                    const isInProg = m.status === 'IN_PROGRESS';
                    return (
                      <div
                        key={m.id}
                        onClick={() => {
                          const nextSt: MilestoneStatus = isDone ? 'PENDING' : isInProg ? 'COMPLETED' : 'IN_PROGRESS';
                          updateMilestoneMutation.mutate({ milestoneId: m.id, status: nextSt });
                        }}
                        className={`p-2.5 rounded-lg border text-[11px] cursor-pointer transition-all ${
                          isDone
                            ? 'bg-emerald-950/60 border-emerald-800 text-emerald-300'
                            : isInProg
                            ? 'bg-brand-950/60 border-brand-700 text-brand-300 ring-1 ring-brand-500'
                            : 'bg-slate-900 border-slate-800 text-slate-500 hover:border-slate-700'
                        }`}
                        title="Click to toggle milestone state"
                      >
                        <div className="font-mono text-[10px] font-bold">Stage {m.orderIndex}</div>
                        <div className="font-bold truncate mt-0.5">{m.title}</div>
                        <div className="text-[9px] font-mono uppercase mt-1">
                          {isDone ? '✓ Done' : isInProg ? '● Active' : '○ Pending'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Project Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-950 rounded-2xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-800">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="font-serif text-xl font-bold text-white">
                Project Settings: {selectedProject.projectCode}
              </h3>
              <button onClick={() => setSelectedProject(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                  Overall Project Status
                </label>
                <select
                  value={projectStatus}
                  onChange={(e) => setProjectStatus(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                >
                  {PUBLISHING_STAGES_CONFIG.map((s) => (
                    <option key={s.status} value={s.status}>{s.order}. {s.label}</option>
                  ))}
                  <option value="COMPLETED">Completed &amp; Archived</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                  Assigned Senior Editor / Lead
                </label>
                <select
                  value={assignedStaffId}
                  onChange={(e) => setAssignedStaffId(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                >
                  <option value="">Unassigned (Committee Pool)</option>
                  {staffList.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.role})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                  Target Publication Date
                </label>
                <input
                  type="date"
                  value={targetPubDate}
                  onChange={(e) => setTargetPubDate(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-400 uppercase font-mono mb-1">
                  Staff Notes
                </label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal milestones notes..."
                  className="w-full px-3.5 py-2.5 rounded-lg bg-slate-900 border border-slate-800 text-white focus:outline-hidden"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <button
                onClick={() => setSelectedProject(null)}
                className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-900"
              >
                Cancel
              </button>
              <button
                onClick={() => updateProjectMutation.mutate()}
                disabled={updateProjectMutation.isPending}
                className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs"
              >
                Update Project Status
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
